import type { ComponentProps } from 'react';

import { cn, cva, type VariantProps } from '../../shared';
import { fieldSurface } from './field-surface';

export const inputVariants = cva([...fieldSurface], {
  variants: {
    inputSize: {
      sm: 'h-control-sm px-input text-caption',
      md: 'h-control px-input text-body',
      lg: 'h-control-lg px-input text-title',
    },
  },
  defaultVariants: { inputSize: 'md' },
});

type InputVariants = VariantProps<typeof inputVariants>;

/**
 * Types that render as a text field. A checkbox, radio, range, file or colour
 * input is a different control with different sizing and a different label
 * relationship — styling one as a text field produces something that looks
 * right and behaves wrongly.
 */
export type InputType =
  | 'text'
  | 'email'
  | 'password'
  | 'search'
  | 'tel'
  | 'url'
  | 'number'
  /**
   * No `date`. A calendar day goes through `DatePicker`, and the reason is the
   * value rather than the styling: the native control reports a bare
   * `yyyy-MM-dd` **string**, which walks straight past the `CalendarDate` and
   * `Instant` types the domain layer exists to make unavoidable. Measured beside
   * ours, it also renders outside the token system entirely — no border, no
   * background, a 16px font of its own and its own glyph.
   *
   * `time`, `datetime-local`, `month` and `week` stay for now because there is
   * nothing to send a caller to instead. They carry the same problem.
   */
  | 'time'
  | 'datetime-local'
  | 'month'
  | 'week';

export interface InputProps extends Omit<ComponentProps<'input'>, 'type'> {
  /**
   * Named `inputSize` because `size` is a real HTML attribute on `<input>` that
   * sets a character width. Both stay reachable.
   */
  inputSize?: NonNullable<InputVariants['inputSize']>;
  type?: InputType;
}

/**
 * A bare styled `<input>`, so a form library's field object spreads onto it
 * directly. Leading and trailing content belongs to a composed field, not here:
 * a wrapper element would make `className` ambiguous between the box and the
 * control.
 */
export function Input({ inputSize, className, type = 'text', ...props }: InputProps) {
  return <input type={type} className={cn(inputVariants({ inputSize }), className)} {...props} />;
}
